import {
  useGetOpportunitiesByVisitorIdQuery,
  useGetRevenueByVisitorIdQuery,
  useGetTotalRevenueByVisitorIdQuery,
  useGetVisitorDetailQuery
} from 'app/apis/queryApi';
import { RootState } from 'app/store';
import { FieldCell } from 'common/components/table/cell-formatters/fieldCell';
import { filterOutNonViewableFields } from 'common/components/table/cell-formatters/fieldHelpers';
import AccordionItem from 'common/components/accordion/accordion';
import { closeSideBarAccordion } from 'features/sidebar/sidebarTogglingSlice';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  GeneralHeader,
  VisitorSessionHeader
} from 'features/visitors/components/accordionHeaderComponents';
import { formatIsoDateStringToDisplayTimeOnly } from 'common/utils/LEGACY_timeHelpers';
import Loading from 'common/components/loading/loading';
import {
  VisitorNumberTile,
  VisitorTileData
} from 'features/visitors/components/visitorNumberTile';
import Tile from 'features/tiles/tile';
import fullNameClosestMatchOnKeyName from 'common/utils/fullNameClosestMatchOnKeyName';
import emailClosestMatchOnKeyName from 'common/utils/emailClosestMatchOnKeyName';
import getProfileImage from 'common/utils/getProfileImage';
import phoneClosestMatchOnKeyName from 'common/utils/phoneClosestMatchOnKeyName';
import jobTitleClosestMatchOnKeyName from 'common/utils/jobTitleClosestMatchOnKeyName';
import AccordionGroup from 'features/visitors/components/accordionGroupComponent';
import { useDebounce, useMeasure } from 'react-use';
import { GoogleStaticMap } from 'features/maps/google-static-maps/googleStaticMap';
import {
  focusLocation,
  selectMapStateByComponentId
} from 'features/maps/google-static-maps/googleStaticMapSlice';
import OperatingSystemLogo from 'common/components/icons/operatingSystemLogo';
import BrowserLogo from 'common/components/icons/browserLogo';
import Blur from 'features/blur/blur';
import { selectCurrencyCode } from 'features/settings/settingsSlice';
import MarketingSourceLogo from 'common/components/icons/marketingSourceLogo';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';
import { AccordionTheme } from 'common/components/accordion/accordion-theme';

const componentId = 'visitor-component';
const sensitiveCallKeys = ['CallerNumber', 'CalledNumber'];

interface VisitorHeaderComponentContent {
  dateTime: string;
  itemCount: number;
  header: JSX.Element;
  tags: JSX.Element;
  id: string;
  action: string;
  touchpointTypes: string[];
  additionalInformation: JSX.Element[];
  theme: AccordionTheme;
}

const fieldKeysWhichDoNotBlur = ['source'];
function shouldBlurField(key: string): boolean {
  return !fieldKeysWhichDoNotBlur.includes(key);
}

const Visitor: FC = () => {
  const { visitorId } = useParams();
  const dispatch = useDispatch();
  const isAccordionClosed = useSelector(
    (state: RootState) => state.sidebar.indexOfOpenAccordion
  );
  const { siteId, currency } = useSelector(
    (state: RootState) => state.selectedSite
  );

  const selectedCurrencyCode = useSelector(selectCurrencyCode);

  const {
    data: visitorDetailData,
    isLoading: isVisitorDetailLoading,
    isSuccess: isVisitorDetailFinished
  } = useGetVisitorDetailQuery({
    siteId,
    visitorId,
    currencyCode: selectedCurrencyCode
  });

  const { data: visitorRevenueData, isLoading: isVisitorRevenueLoading } =
    useGetRevenueByVisitorIdQuery({
      siteId,
      visitorId,
      currency: selectedCurrencyCode
    });

  const {
    data: visitorOpportunitiesData,
    isLoading: isVisitorOpportunitiesLoading
  } = useGetOpportunitiesByVisitorIdQuery({
    siteId,
    visitorId,
    currency: selectedCurrencyCode
  });

  const { data: visitorDetailRevenueData, isLoading: isRevenueLoading } =
    useGetTotalRevenueByVisitorIdQuery({
      siteId,
      visitorId,
      currencyCode: selectedCurrencyCode
    });

  const {
    sessions,
    profileDetails,
    webData,
    inboundCallData,
    urlParameters,
    webCookies
  } = useMemo(() => {
    const web = visitorDetailData?.conversionDetails.filter(
      (conversionDetail) => conversionDetail.conversionType === 'Web'
    );
    const inboundCall = visitorDetailData?.conversionDetails.filter(
      (conversionDetail) => conversionDetail.conversionType === 'InboundCall'
    );
    const touchpointSessions = visitorDetailData?.sessions.map((session) => {
      return {
        ...session,
        dateTime: session.startDateTime,
        itemCount: session.webPageViews.length,
        additionalInformation: session.webPageViews.map((pageView, index) => {
          return (
            <div
              className="grid grid-cols-12 w-full last:rounded-b even:bg-neutral-50 pr-10"
              key={index}
            >
              <div className="pl-2 p-2 col-span-3 text-left text-sm font-semibold">
                {formatIsoDateStringToDisplayTimeOnly(pageView.captureDateTime)}
              </div>
              <div className="p-2 col-span-9 text-left">
                <a
                  href={pageView.fullUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary-100 underline"
                >
                  <Blur>
                    <span>{pageView.localPath}</span>
                  </Blur>
                </a>
                {filterOutNonViewableFields(
                  Object.entries(pageView.payloads)
                ).map(([_, { key, value }], index) => {
                  if (shouldBlurField(key)) {
                    return (
                      <Blur key={index}>
                        <FieldCell
                          key={index}
                          identifier={key}
                          value={value}
                          bgColor="bg-tertiary-200"
                        />
                      </Blur>
                    );
                  }
                  return (
                    <FieldCell
                      key={index}
                      identifier={key}
                      value={value}
                      bgColor="bg-tertiary-200"
                    />
                  );
                })}
                {filterOutNonViewableFields(
                  Object.entries(pageView.queryStringParams)
                ).map(([_, { key, value }], index) => {
                  return (
                    <Blur key={index}>
                      <FieldCell key={index} identifier={key} value={value} />
                    </Blur>
                  );
                })}
                {pageView.relatedCalls.map((item, index) => {
                  return (
                    <Blur key={index}>
                      <FieldCell key={index} identifier="Call" value={item} />
                    </Blur>
                  );
                })}
              </div>
            </div>
          );
        }),
        header: (
          <>
            <MarketingSourceLogo
              marketingSource={session.marketingSource}
              iconClasses="text-lg flex mr-2"
              imageClasses="w-3 h-3 mr-2"
            />
            <div>
              {session.marketingSource}
              {session.referrerUrl ? (
                <a
                  href={session.referrerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-primary-100 italic pl-2 hover:underline"
                >
                  <span>{session.referrerUrl}</span>
                </a>
              ) : (
                <></>
              )}
              {session.offlineSource ? (
                <span className="text-xs text-primary-400 italic pl-2">
                  {session.offlineSource}
                </span>
              ) : (
                <></>
              )}
            </div>
          </>
        ),
        tags: (
          <>
            <span
              className={`${
                session.touchpointTypes.includes('InboundCall') &&
                session.action === 'convert'
                  ? 'bg-off-blue'
                  : ''
              } capitalize shrink text-blue py-0.5 px-3 rounded text-sm`}
            >
              {session.touchpointTypes.includes('InboundCall') &&
              session.action === 'convert'
                ? 'Call'
                : null}
            </span>
            <span
              className={`${
                session.touchpointTypes.includes('Web') &&
                session.action === 'convert'
                  ? 'bg-tertiary-200'
                  : ''
              } capitalize shrink text-tertiary-600 py-0.5 px-3 rounded text-sm`}
            >
              {session.touchpointTypes.includes('Web') &&
              session.action === 'convert'
                ? 'Form'
                : null}
            </span>
          </>
        ),
        theme: AccordionTheme.CONVERSIONS
      } as VisitorHeaderComponentContent;
    });

    const revenueSessions = visitorRevenueData?.map((session) => {
      return {
        ...session,
        dateTime: session.revenueClosedDateTimeIso,
        itemCount: 0,
        additionalInformation: [
          <div
            className="grid grid-cols-12 w-full last:rounded-b even:bg-neutral-50 pr-10"
            key={1}
          >
            <div className="pl-2 p-2 col-span-3 text-left text-sm font-semibold">
              {formatIsoDateStringToDisplayTimeOnly(
                session.revenueClosedDateTimeIso
              )}
            </div>
            <div className="p-2 col-span-9 text-left">
              {session.labels.map((item, index) => {
                return (
                  <Blur key={index}>
                    <FieldCell key={index} identifier="Label" value={item} />
                  </Blur>
                );
              })}
              {session.emailAddress && (
                <Blur key={session.emailAddress}>
                  <FieldCell
                    key={session.emailAddress}
                    identifier="Email Address"
                    value={session.emailAddress}
                  />
                </Blur>
              )}
              {session.phoneNumber && (
                <Blur key={session.phoneNumber}>
                  <FieldCell
                    key={session.phoneNumber}
                    identifier="Phone Number"
                    value={session.phoneNumber}
                  />
                </Blur>
              )}
              {session.raid && (
                <Blur key={session.raid}>
                  <FieldCell
                    key={session.raid}
                    identifier="RaId"
                    value={session.raid}
                  />
                </Blur>
              )}
              {session.payloadValue && (
                <Blur key={session.payloadValue}>
                  <FieldCell
                    key={session.payloadValue}
                    identifier="Payload"
                    value={session.payloadValue}
                  />
                </Blur>
              )}
            </div>
          </div>
        ],
        header: (
          <>
            <div className="font-semibold">
              <CurrencyDisplayWrapper currencyCode={selectedCurrencyCode}>
                <>
                  {Intl.NumberFormat('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(session.value)}
                </>
              </CurrencyDisplayWrapper>
            </div>
          </>
        ),
        tags: (
          <span className="bg-secondary-50 capitalize shrink text-secondary-200 py-0.5 px-3 rounded text-sm">
            Revenue
          </span>
        ),
        id: session.revenueId,
        touchpointTypes: [],
        action: 'track',
        theme: AccordionTheme.REVENUE
      } as VisitorHeaderComponentContent;
    });

    const opportunitySessions = visitorOpportunitiesData?.map((session) => {
      return {
        ...session,
        dateTime: session.opportunityDateTimeIso,
        itemCount: 0,
        additionalInformation: [
          <div
            className="grid grid-cols-12 w-full last:rounded-b even:bg-neutral-50 pr-10"
            key={1}
          >
            <div className="pl-2 p-2 col-span-3 text-left text-sm font-semibold">
              {formatIsoDateStringToDisplayTimeOnly(
                session.opportunityDateTimeIso
              )}
            </div>
            <div className="p-2 col-span-9 text-left">
              {session.labels.map((item, index) => {
                return (
                  <Blur key={index}>
                    <FieldCell key={index} identifier="Label" value={item} />
                  </Blur>
                );
              })}
              {session.emailAddress && (
                <Blur key={session.emailAddress}>
                  <FieldCell
                    key={session.emailAddress}
                    identifier="Email Address"
                    value={session.emailAddress}
                  />
                </Blur>
              )}
              {session.phoneNumber && (
                <Blur key={session.phoneNumber}>
                  <FieldCell
                    key={session.phoneNumber}
                    identifier="Phone Number"
                    value={session.phoneNumber}
                  />
                </Blur>
              )}
              {session.raid && (
                <Blur key={session.raid}>
                  <FieldCell
                    key={session.raid}
                    identifier="RaId"
                    value={session.raid}
                  />
                </Blur>
              )}
              {session.payloadValue && (
                <Blur key={session.payloadValue}>
                  <FieldCell
                    key={session.payloadValue}
                    identifier="Payload"
                    value={session.payloadValue}
                  />
                </Blur>
              )}
            </div>
          </div>
        ],
        header: (
          <>
            <div className="font-semibold">
              <CurrencyDisplayWrapper currencyCode={selectedCurrencyCode}>
                <>
                  {Intl.NumberFormat('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  }).format(session.value)}
                </>
              </CurrencyDisplayWrapper>
            </div>
            <div className="pl-2 italic text-xs">{session.pipelineStage}</div>
          </>
        ),
        tags: (
          <span className="bg-primary-45 text-primary-600 capitalize shrink py-0.5 px-3 rounded text-sm">
            Opportunity
          </span>
        ),
        id: session.opportunityId,
        touchpointTypes: [],
        action: 'track',
        theme: AccordionTheme.OPPORTUNITY
      } as VisitorHeaderComponentContent;
    });

    return {
      ...visitorDetailData,
      webData:
        web
          ?.slice()
          .sort(
            (a, b) =>
              new Date(b.captureDateTime).valueOf() -
              new Date(a.captureDateTime).valueOf()
          ) ?? [],
      inboundCallData: inboundCall ?? [],
      sessions:
        touchpointSessions
          ?.concat(revenueSessions ?? [])
          ?.concat(opportunitySessions ?? [])
          ?.slice()
          .sort(
            (a, b) =>
              new Date(b.dateTime).valueOf() - new Date(a.dateTime).valueOf()
          ) ?? []
    };
  }, [
    visitorDetailData,
    visitorRevenueData,
    visitorOpportunitiesData,
    selectedCurrencyCode
  ]);

  const pageViews = sessions
    .flatMap((session) => session.itemCount)
    .reduce((all, currentNumber) => all + currentNumber, 0);

  const totalSessions = sessions.length;

  const latestConversionTouchpointTypes =
    sessions
      .find((x) => x.action === 'convert')
      ?.touchpointTypes.map(translateTouchpointType)
      .join(', ') ?? '';

  function translateTouchpointType(touchpointType: string): string {
    switch (touchpointType) {
      case 'Web':
        return 'Form';
      case 'InboundCall':
        return 'Call';
      default:
        return touchpointType;
    }
  }

  const [mapRef, { width, height }] = useMeasure<HTMLDivElement>();

  const [mapWidth, setMapWidth] = useState<number>(width);
  const [mapHeight, setMapHeight] = useState<number>(height);

  const { currentFocusLocation, isInGlobalFocusMode } = useSelector(
    (state: RootState) => selectMapStateByComponentId(state, componentId)
  );

  useDebounce(
    () => {
      const adjustedWidth = width < 640 ? width : 640;
      const adjustedHeight = height < 640 ? height : 640;

      setMapWidth(Math.round(adjustedWidth));
      setMapHeight(Math.round(adjustedHeight));
    },
    500,
    [width, height]
  );

  const useMountEffect = () =>
    useEffect(() => {
      if (isAccordionClosed !== -1) {
        dispatch(closeSideBarAccordion());
      }
    }, []);

  useMountEffect();

  const getDataForTile = (
    tileTitle: string,
    count: number | string,
    metric: string | number
  ): VisitorTileData => {
    return {
      label: tileTitle,
      count,
      metric
    };
  };

  return (
    <>
      <p className="pr-2 align-bottom leading-none text-primary-700 text-xl capitalize mb-4">
        Visitor Journey
      </p>
      {isVisitorDetailLoading ? (
        <Loading />
      ) : (
        <>
          <div className="relative flex flex-col lg:flex-row items-start">
            {/* page layout - grids */}
            <div className="grid grid-cols-2 gap-2 lg:w-2/3 lg:mr-2">
              {/* container for face card and tiles and session */}
              <div className="col-span-2 grid  grid-cols-12 gap-2 items-stretch h-full">
                <div className="col-span-12  md:col-span-5 lg:col-span-5 bg-white rounded border-neutral-200 border shadow-sm h-full">
                  <div ref={mapRef} className="bg-neutral-150 h-60">
                    {isVisitorDetailFinished &&
                    visitorDetailData?.locationDetails[0] ? (
                      visitorDetailData?.locationDetails[0]?.latitude &&
                      visitorDetailData?.locationDetails[0]?.longitude &&
                      isVisitorDetailFinished &&
                      mapWidth &&
                      mapHeight ? (
                        <>
                          <GoogleStaticMap
                            componentId={componentId}
                            mapHeight={mapHeight}
                            mapWidth={mapWidth}
                            locations={visitorDetailData.locationDetails}
                          />
                        </>
                      ) : (
                        <Loading />
                      )
                    ) : (
                      <p
                        className="h-full w-full rounded-t relative z-20 bg-cover"
                        style={{
                          backgroundImage: 'url("/media/misc/europe.png")'
                        }}
                      ></p>
                    )}
                  </div>
                  <div className="flex flex-col justify-between p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <Blur>
                          <p className="font-semibold text-sm capitalize">
                            {fullNameClosestMatchOnKeyName(webData)}
                          </p>
                          <p className="text-sm capitalize">
                            {jobTitleClosestMatchOnKeyName(webData)}
                          </p>
                        </Blur>
                      </div>
                      <div className="flex items-center justify-center h-14 w-14">
                        <Blur>
                          <img
                            className="rounded-full border-neutral-200 border-2"
                            src={getProfileImage(
                              emailClosestMatchOnKeyName(webData)
                            )}
                          />
                        </Blur>
                      </div>
                    </div>
                    <div className="mt-10">
                      <p className="flex items-center text-xs mb-2 break-all">
                        <i className="ra-icons-generic-envelope-2 text-primary-700 mr-2 flex"></i>
                        <Blur>
                          <span> {emailClosestMatchOnKeyName(webData)}</span>
                        </Blur>
                      </p>
                      {visitorDetailData?.locationDetails.map(
                        (locationDetail, index) => {
                          const location = `${locationDetail.city}, ${locationDetail.regionName}, ${locationDetail.country}`;
                          return (
                            <button
                              onClick={() =>
                                dispatch(
                                  focusLocation({
                                    componentId,
                                    focusLocation: location
                                  })
                                )
                              }
                              key={index}
                              className={`flex items-center text-xs mb-2 block hover:underline${
                                !isInGlobalFocusMode &&
                                visitorDetailData?.locationDetails.length > 1 &&
                                currentFocusLocation === location
                                  ? ' underline'
                                  : ''
                              }`}
                            >
                              <i className="ra-icons-generic-maps-and-flags-1 mr-2 flex text-primary-700"></i>
                              <Blur>
                                <span>
                                  {`${locationDetail.city}, ${locationDetail.country}`}
                                </span>
                              </Blur>
                            </button>
                          );
                        }
                      )}

                      <p className="text-xs flex">
                        <i className="ra-icons-generic-telephone text-xs text-primary-700 mr-2"></i>
                        <Blur>
                          <span>
                            {phoneClosestMatchOnKeyName(webData) ??
                              phoneClosestMatchOnKeyName(inboundCallData) ??
                              'Unknown Phone Number'}
                          </span>
                        </Blur>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-span-12 md:col-span-7 lg:col-span-7 grid grid-cols-2 gap-2">
                  {/* Foreach tile */}
                  <Tile className="w-full">
                    {!isRevenueLoading && visitorDetailRevenueData != null ? (
                      <VisitorNumberTile
                        tileData={getDataForTile(
                          'Revenue',
                          `${currency ?? ''}${Intl.NumberFormat('en-GB', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          }).format(
                            visitorDetailRevenueData.visitorRevenueTotal
                          )}`,
                          visitorDetailRevenueData.percentageDifference
                        )}
                      />
                    ) : (
                      <Loading />
                    )}
                  </Tile>
                  <Tile className="w-full">
                    <VisitorNumberTile
                      tileData={getDataForTile(
                        latestConversionTouchpointTypes.includes(',')
                          ? 'Latest Conversions'
                          : 'Latest Conversion',
                        latestConversionTouchpointTypes,
                        ''
                      )}
                    />
                  </Tile>
                  <Tile className="w-full">
                    <VisitorNumberTile
                      tileData={getDataForTile('Page Views', pageViews, '')}
                    />
                  </Tile>
                  <Tile className="w-full">
                    <VisitorNumberTile
                      tileData={getDataForTile('Sessions', totalSessions, '')}
                    />
                  </Tile>
                </div>
              </div>

              <div className="col-span-2 lg:col-span-2 grid grid-cols-1 gap-2">
                {/* sessions area */}
                <div className="col-span-2 lg:col-span-2">
                  {!isVisitorRevenueLoading &&
                  visitorRevenueData != null &&
                  !isVisitorOpportunitiesLoading &&
                  visitorOpportunitiesData != null ? (
                    <></>
                  ) : (
                    <div className="pb-2">
                      <Loading />
                    </div>
                  )}
                  {visitorDetailData && !isVisitorDetailLoading ? (
                    <AccordionGroup
                      accordionIds={sessions.map((session) => session.id)}
                    >
                      {sessions?.map((session) => {
                        return (
                          <AccordionItem
                            key={session.id}
                            HeaderComponent={VisitorSessionHeader}
                            headerContent={{
                              startDate: session.dateTime,
                              itemCount: session.itemCount,
                              header: session.header,
                              tags: session.tags
                            }}
                            id={session.id}
                            theme={session.theme}
                          >
                            <div className="w-full">
                              {session.additionalInformation}
                            </div>
                          </AccordionItem>
                        );
                      })}
                    </AccordionGroup>
                  ) : null}
                </div>
              </div>
            </div>
            <div className=" w-full lg:w-1/3">
              <div className="grid grid-flow-row auto-rows-max lg:col-span-1">
                {profileDetails && profileDetails.length > 0 ? (
                  <AccordionItem
                    HeaderComponent={GeneralHeader}
                    headerContent={{
                      title: 'Profile'
                    }}
                    id="Visitor-Profile"
                    theme={AccordionTheme.DEFAULT}
                  >
                    <div className="rounded-b">
                      {profileDetails?.map((profileDetail, index) => {
                        return (
                          <div
                            className="grid grid-cols-6 w-full even:bg-neutral-50 last:rounded-b pr-10 text-xs capitalize"
                            key={index}
                          >
                            <div className="pl-2 p-2 col-span-3 text-left text-xs font-semibold">
                              {profileDetail.key}
                            </div>
                            <div className="pl-2 p-2 col-span-3 flex items-center">
                              {profileDetail.key === 'operating system' ? (
                                <OperatingSystemLogo
                                  operatingSystem={profileDetail.value}
                                  imageClasses="w-3 h-3 mr-1"
                                />
                              ) : (
                                <></>
                              )}
                              {profileDetail.key === 'browser' ? (
                                <BrowserLogo
                                  browser={profileDetail.value}
                                  imageClasses="w-3 h-3 mr-1"
                                />
                              ) : (
                                <></>
                              )}
                              <span className="text-xs break-words">
                                {profileDetail.value}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionItem>
                ) : null}
                {webData && webData.length > 0 ? (
                  <AccordionItem
                    HeaderComponent={GeneralHeader}
                    headerContent={{
                      title: 'Data'
                    }}
                    id="Visitor-WebData"
                    theme={AccordionTheme.DEFAULT}
                  >
                    {webData?.map((conversionData, index) => {
                      return (
                        <div
                          className="grid grid-cols-6 w-full even:bg-neutral-50 pr-10 text-xs"
                          key={index}
                        >
                          <div className="pl-2 p-2 col-span-3 text-left text-xs font-semibold break-words">
                            {conversionData.key}
                          </div>
                          <div className="pl-2 p-2 col-span-3 text-xs break-words">
                            <Blur>
                              <span>{conversionData.value}</span>
                            </Blur>
                          </div>
                        </div>
                      );
                    })}
                  </AccordionItem>
                ) : null}
                {urlParameters && urlParameters.length > 0 ? (
                  <AccordionItem
                    HeaderComponent={GeneralHeader}
                    headerContent={{
                      title: 'URL Parameters'
                    }}
                    id="Visitor-UrlParameters"
                    theme={AccordionTheme.DEFAULT}
                  >
                    <div className="rounded-b">
                      {urlParameters?.map((param, index) => {
                        return (
                          <div
                            className="grid grid-cols-6 w-full even:bg-neutral-50 last:rounded-b pr-10 text-xs"
                            key={index}
                          >
                            <div className="pl-2 p-2 col-span-3 text-left text-xs font-semibold">
                              {param.key}
                            </div>
                            <div className="pl-2 p-2 col-span-3 text-xs break-words">
                              <Blur>
                                <span>{param.value}</span>
                              </Blur>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionItem>
                ) : null}
                {inboundCallData && inboundCallData.length > 0 ? (
                  <AccordionItem
                    HeaderComponent={GeneralHeader}
                    headerContent={{
                      title: 'Phone Call'
                    }}
                    id="Visitor-InboundCallData"
                    theme={AccordionTheme.DEFAULT}
                  >
                    {inboundCallData?.map((conversionData, index) => {
                      return (
                        <div
                          className="grid grid-cols-6 w-full even:bg-neutral-50 pr-10 text-xs"
                          key={index}
                        >
                          <div className="pl-2 p-2 col-span-3 text-left text-xs font-semibold">
                            {conversionData.key}
                          </div>
                          <div className="pl-2 p-2 col-span-3 text-xs break-words">
                            {sensitiveCallKeys.includes(conversionData.key) ? (
                              <Blur>
                                <span> {conversionData.value}</span>
                              </Blur>
                            ) : (
                              conversionData.value
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </AccordionItem>
                ) : null}

                {webCookies && webCookies.length > 0 ? (
                  <AccordionItem
                    HeaderComponent={GeneralHeader}
                    headerContent={{
                      title: 'Cookies'
                    }}
                    id="Visitor-WebCookiesData"
                    theme={AccordionTheme.DEFAULT}
                  >
                    {webCookies?.map((cookie, index) => {
                      return (
                        <div
                          className="grid grid-cols-6 w-full even:bg-neutral-50 pr-10 text-xs"
                          key={index}
                        >
                          <div className="pl-2 p-2 col-span-3 text-left text-xs font-semibold">
                            {cookie.cookieName}
                          </div>
                          <div className="pl-2 p-2 col-span-3 text-xs break-words">
                            {cookie.cookieValue}
                          </div>
                        </div>
                      );
                    })}
                  </AccordionItem>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Visitor;
